import React, { useCallback, useEffect, useState } from "react";
import { Button, Spin, notification } from "antd";
import BasicModal from "../../ui/BasicModal/BasicModal";
import BillingProfileForm from "./BillingProfileForm";
import { billingApi } from "./billingApi";
import { formatProfileAddress } from "./billingProfile";
import { getCountryName } from "./countries";
import { getTaxIdLabel } from "./taxIds";
import "./index.scss";

const TAX_ID_STATUS = {
   verified: "verified",
   pending: "being verified",
   unverified: "couldn't be verified",
   unavailable: "verification unavailable"
};

/**
 * The billing details on the subscription page: who invoices are made out to,
 * where they go, and the tax number printed on them — with the way to change
 * them. Saving a card asks for them too (CardSetup), so an organization that has
 * a card already has these.
 */
function BillingDetailsPanel({ organizationId }) {
   const [profile, setProfile] = useState(null);
   const [isLoading, setIsLoading] = useState(true);
   const [isEditing, setIsEditing] = useState(false);

   const load = useCallback(() => {
      setIsLoading(true);

      billingApi
         .getBillingProfile(organizationId)
         .then(setProfile)
         // No profile yet reads the same as an empty one: the panel offers to add it.
         .catch(() => setProfile(null))
         .finally(() => setIsLoading(false));
   }, [organizationId]);

   useEffect(() => {
      load();
   }, [load]);

   const handleSaved = (saved) => {
      setProfile(saved);
      setIsEditing(false);
      notification.success({
         message: "Billing details saved successfully",
         description: "They're used on every invoice from now on."
      });
   };

   const taxIdStatus = TAX_ID_STATUS[profile?.taxIdVerificationStatus];

   return (
      <section className="billing-details">
         <div className="billing-details__header">
            <h3 className="billing-details__title">Billing details</h3>
            {!isLoading && (
               <Button className="defaultButton" onClick={() => setIsEditing(true)}>
                  {profile?.isComplete ? "Edit billing details" : "Add billing details"}
               </Button>
            )}
         </div>

         {isLoading ? (
            <Spin />
         ) : profile?.isComplete ? (
            <dl className="billing-details__rows">
               <dt>Billed to</dt>
               <dd>{profile.name}</dd>
               <dt>Invoice email</dt>
               <dd>{profile.email}</dd>
               <dt>Address</dt>
               <dd>{formatProfileAddress(profile, getCountryName)}</dd>
               {profile.taxId?.value && (
                  <>
                     <dt>{getTaxIdLabel(profile.address?.country)}</dt>
                     <dd>
                        {profile.taxId.value}
                        {taxIdStatus ? ` (${taxIdStatus})` : ""}
                     </dd>
                  </>
               )}
            </dl>
         ) : (
            <p className="billing-panel__text">
               Add who invoices are made out to. A valid invoice needs a billing address, so a card
               can't be saved without one.
            </p>
         )}

         {isEditing && (
            <BasicModal isModalOpen={isEditing} handleCancel={() => setIsEditing(false)}>
               <h3 className="billing-section-title">Billing details</h3>
               <BillingProfileForm
                  organizationId={organizationId}
                  onSaved={handleSaved}
                  onCancel={() => setIsEditing(false)}
               />
            </BasicModal>
         )}
      </section>
   );
}

export default BillingDetailsPanel;
